import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTelegram } from '../../context/TelegramContext';
import { useTranslation } from 'react-i18next';
import io from 'socket.io-client';

// Основные стили компонентов
const NotificationContainer = styled.div`
  position: fixed;
  background: white;
  border-radius: 16px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  overflow: hidden;
  display: ${props => props.isOpen ? 'block' : 'none'};
  backdrop-filter: blur(10px);
  
  @media (min-width: 577px) {
    top: 120px;
    right: 20px;
    width: 380px;
    max-height: 520px;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 576px) {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }
`;

const NotificationIcon = styled.div`
  width: 36px;
  height: 36px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  box-shadow: 
    4px 4px 12px rgba(0, 0, 0, 0.06),
    -4px -4px 12px rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      6px 6px 16px rgba(0, 0, 0, 0.08),
      -6px -6px 16px rgba(255, 255, 255, 0.9);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 
      2px 2px 6px rgba(0, 0, 0, 0.04),
      -2px -2px 6px rgba(255, 255, 255, 0.6);
  }
  
  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
    
    &:hover {
      transform: none;
    }
  }
`;

const BellIcon = styled.svg`
  width: 18px;
  height: 18px;
  fill: #4a6fa1;
  transition: all 0.3s ease;
  
  ${NotificationIcon}:hover & {
    fill: #2c5aa0;
    transform: scale(1.1);
  }
  
  @media (max-width: 576px) {
    width: 16px;
    height: 16px;
    
    ${NotificationIcon}:hover & {
      transform: none;
    }
  }
`;

const NotificationBadge = styled.span`
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
  border-radius: 12px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -6px;
  right: -6px;
  border: 2px solid white;
  box-shadow: 
    2px 2px 6px rgba(255, 71, 87, 0.3),
    0 0 0 1px rgba(255, 71, 87, 0.1);
  animation: ${props => props.pulse ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
  
  @media (max-width: 576px) {
    top: -4px;
    right: -4px;
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    padding: 1px 5px;
  }
`;

const NotificationHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8f9fa, #ffffff);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
  
  @media (max-width: 576px) {
    padding: 16px 20px;
    padding-top: calc(16px + env(safe-area-inset-top, 0px));
  }
`;

const NotificationList = styled.div`
  overflow-y: auto;
  
  @media (min-width: 577px) {
    max-height: 440px;
  }
  
  @media (max-width: 576px) {
    height: calc(100vh - 80px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
    -webkit-overflow-scrolling: touch;
  }
`;

const NotificationItem = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  background: ${props => props.isRead ? '#ffffff' : 'linear-gradient(135deg, #f8f9ff, #ffffff)'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  ${props => !props.isRead && `
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(135deg, #007bff, #0056b3);
    }
  `}
  
  &:hover {
    background: ${props => props.isRead ? '#f8f9fa' : 'linear-gradient(135deg, #f0f4ff, #f8f9fa)'};
    transform: translateX(2px);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 576px) {
    padding: 16px 20px;
    
    &:hover {
      background: ${props => props.isRead ? '#ffffff' : 'linear-gradient(135deg, #f8f9ff, #ffffff)'};
      transform: none;
    }
    
    &:active {
      background: #f0f0f0;
    }
  }
`;

const NotificationTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.3;
`;

const NotificationMessage = styled.p`
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #5a6c7d;
  line-height: 1.5;
  padding-left: 28px;
`;

const NotificationTime = styled.span`
  font-size: 11px;
  color: #8b9dc3;
  font-weight: 500;
  padding-left: 28px;
`;

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: #8b9dc3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 28px;
    height: 28px;
    fill: #adb5bd;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #007bff, #0056b3);
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, #0056b3, #004085);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(108, 117, 125, 0.1);
    color: #495057;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 576px) {
    font-size: 24px;
    width: 40px;
    height: 40px;
    
    &:hover {
      background: none;
      transform: none;
    }
    
    &:active {
      background: rgba(108, 117, 125, 0.1);
    }
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  backdrop-filter: blur(4px);
  
  @media (min-width: 577px) {
    display: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Кастомные SVG иконки
const BellIconComponent = ({ hasUnread }) => (
  <BellIcon 
    viewBox="0 0 24 24" 
    xmlns="http://www.w3.org/2000/svg"
    style={{
      animation: hasUnread ? 'shake 0.5s ease-in-out' : 'none'
    }}
  >
    <style jsx>{`
      @keyframes shake {
        0%, 100% { transform: rotate(0deg); }
        25% { transform: rotate(-5deg); }
        75% { transform: rotate(5deg); }
      }
    `}</style>
    
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z"/>
    <path d="M18 16v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
    <circle cx="12" cy="2" r="1" opacity="0.6"/>
    
    {hasUnread && (
      <>
        <circle cx="19" cy="8" r="2" fill="#ff4757" opacity="0.3">
          <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="19" cy="8" r="1" fill="#ff4757" opacity="0.6">
          <animate attributeName="r" values="1;3;1" dur="2s" begin="0.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" begin="0.5s" repeatCount="indefinite"/>
        </circle>
      </>
    )}
  </BellIcon>
);
const BudgetAlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    {/* Основной круг */}
    <circle cx="12" cy="12" r="10" fill="#dc3545" fillOpacity="0.1" stroke="#dc3545" strokeWidth="2"/>
    
    {/* Знак доллара/валюты */}
    <path d="M12 7v1m0 8v1m-3-5h6m-3-3a3 3 0 0 1 3 3m-6 0a3 3 0 0 1 3-3" 
          stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    
    {/* Восклицательный знак предупреждения */}
    <circle cx="18" cy="6" r="3" fill="#ff6b6b"/>
    <path d="M18 4v2m0 2h.01" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// 🎯 Улучшенная иконка целей с прогрессом
const GoalProgressIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    {/* Внешний круг цели */}
    <circle cx="12" cy="12" r="10" fill="#28a745" fillOpacity="0.1" stroke="#28a745" strokeWidth="2"/>
    
    {/* Средний круг */}
    <circle cx="12" cy="12" r="6" fill="#28a745" fillOpacity="0.2" stroke="#28a745" strokeWidth="1.5"/>
    
    {/* Центр цели */}
    <circle cx="12" cy="12" r="2" fill="#28a745"/>
    
    {/* Стрелка попадания */}
    <path d="M7 7l8 8m-8 0l8-8" stroke="#28a745" strokeWidth="2" strokeLinecap="round"/>
    
    {/* Галочка успеха */}
    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ⏰ Улучшенная иконка напоминания с колокольчиком
const ReminderClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    {/* Основной циферблат */}
    <circle cx="12" cy="12" r="9" fill="#ffc107" fillOpacity="0.1" stroke="#ffc107" strokeWidth="2"/>
    
    {/* Стрелки часов */}
    <path d="M12 7v5l3 3" stroke="#ffc107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    
    {/* Маленький колокольчик в углу */}
    <g transform="translate(16, 4) scale(0.5)">
      <path d="M8 6c0-2.2 1.8-4 4-4s4 1.8 4 4c0 4.4 2 8 2 8H6s2-3.6 2-8z" 
            fill="#ff6b6b" stroke="#ff6b6b" strokeWidth="1"/>
      <path d="M10 20c0 1.1.9 2 2 2s2-.9 2-2" stroke="#ff6b6b" strokeWidth="1" fill="none"/>
    </g>
    
    {/* Точки на циферблате */}
    <circle cx="12" cy="4" r="1" fill="#ffc107"/>
    <circle cx="20" cy="12" r="1" fill="#ffc107"/>
    <circle cx="12" cy="20" r="1" fill="#ffc107"/>
    <circle cx="4" cy="12" r="1" fill="#ffc107"/>
  </svg>
);

// 📢 Улучшенная системная иконка с информацией
const SystemInfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    {/* Основной круг */}
    <circle cx="12" cy="12" r="10" fill="#17a2b8" fillOpacity="0.1" stroke="#17a2b8" strokeWidth="2"/>
    
    {/* Символ информации */}
    <path d="M12 8v8m0-12h.01" stroke="#17a2b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    
    {/* Декоративные элементы */}
    <circle cx="12" cy="6" r="1" fill="#17a2b8"/>
    
    {/* Маленькие точки вокруг */}
    <circle cx="6" cy="6" r="1" fill="#17a2b8" fillOpacity="0.3"/>
    <circle cx="18" cy="6" r="1" fill="#17a2b8" fillOpacity="0.3"/>
    <circle cx="6" cy="18" r="1" fill="#17a2b8" fillOpacity="0.3"/>
    <circle cx="18" cy="18" r="1" fill="#17a2b8" fillOpacity="0.3"/>
  </svg>
);

// ❌ Улучшенная иконка закрытия
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.1"/>
    <path d="M8 8l8 8m0-8l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);




const SuccessIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#28a745" fillOpacity="0.1" stroke="#28a745" strokeWidth="2"/>
    <path d="M9 12l2 2 4-4" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    
    {/* Маленькие звездочки вокруг */}
    <g stroke="#28a745" strokeWidth="1" opacity="0.6">
      <path d="M6 6l1 1m10-1l-1 1m1 10l-1-1m-10 1l1-1"/>
    </g>
  </svg>
);

// Иконка для предупреждений
const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L2 20h20L12 2z" fill="#ffc107" fillOpacity="0.1" stroke="#ffc107" strokeWidth="2"/>
    <path d="M12 8v4m0 4h.01" stroke="#ffc107" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const MoneyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#28a745">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
    <path d="M12.5 7H11v2H9v2h2v2h1.5V9.5c.83 0 1.5.67 1.5 1.5h2c0-1.93-1.57-3.5-3.5-3.5V7z"/>
    <path d="M12.5 13v2H11v2h1.5v-1.5c.83 0 1.5-.67 1.5-1.5h-2z"/>
  </svg>
);

const TargetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#17a2b8">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 7a5 5 0 0 0-5 5h2a3 3 0 0 1 3-3V7z"/>
    <path d="M17 12a5 5 0 0 1-5 5v-2a3 3 0 0 0 3-3h2z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#ffc107">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
    <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
  </svg>
);

const SystemIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#6c757d">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v2h-2v-2zm0-8h2v6h-2V9z"/>
  </svg>
);

const EmptyBellIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2z"/>
    <path d="M18 16v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
    <path d="M12 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" opacity="0.3"/>
  </svg>
);


// Основной компонент
const NotificationCenter = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (telegramUserId) {
      loadNotifications();
      initializeSocket();
    }
    
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [telegramUserId]);

  const initializeSocket = () => {
    const newSocket = io('https://fftg.farodinprojects.org:3000', {
      query: { user_id: telegramUserId },
      transports: ['websocket'],  // Явно указываем транспорт
      secure: true                // Указываем что соединение должно быть защищённым
    });

    newSocket.on('new_notification', (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      showToastNotification(notification);
    });

    newSocket.on('connect', () => {
      console.log('[NotificationCenter] WebSocket connected');
    });

    newSocket.on('disconnect', () => {
      console.log('[NotificationCenter] WebSocket disconnected');
    });

    setSocket(newSocket);
  };

  const loadNotifications = async () => {
    if (!telegramUserId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://fftg.farodinprojects.org:3000/get_notifications?user_id=${telegramUserId}&limit=50`
      );
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.notifications?.filter(n => !n.is_read).length || 0);
      }
    } catch (error) {
      console.error('[NotificationCenter] Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    if (!telegramUserId) return;
    
    try {
      await fetch('https://fftg.farodinprojects.org:3000/mark_notification_read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUserId,
          notification_id: notificationId
        })
      });

      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[NotificationCenter] Error marking notification as read:', error);
    }
  };
const getLocalizedMessage = (notification) => {
  const { type, message, data } = notification;
  
  // Для бюджетных уведомлений
  if (type === 'budget_exceeded' || type === 'budget' || type === 'budget_warning') {
    // Подготавливаем данные для локализации
    let localizedData = { 
      category: 'Неизвестно', 
      percentage: '0', 
      remaining: '' 
    };
    
    // Если есть данные в формате объекта
    if (data && typeof data === 'object') {
      localizedData.category = data.category || 'Неизвестно';
      localizedData.percentage = data.percentage || '0';
      localizedData.remaining = data.remaining ? `${data.remaining} ${data.currency || ''}` : '';
    }
    // Если нет данных, извлекаем из текста сообщения
    else {
      const categoryMatch = message.match(/категории\s+['"]([^'"]+)['"]/i) || message.match(/категорії\s+['"]([^'"]+)['"]/i);
      const percentageMatch = message.match(/(\d+(?:\.\d+)?)\s*%/i);
      const remainingMatch = message.match(/Осталось:\s+(.+)$/i) || message.match(/Залишилось:\s+(.+)$/i);
      
      if (categoryMatch) localizedData.category = categoryMatch[1];
      if (percentageMatch) localizedData.percentage = percentageMatch[1];
      if (remainingMatch) localizedData.remaining = remainingMatch[1];
    }
    
    // Получаем шаблон и делаем замену вручную
    const template = t('budget_notification_message');
    if (template === 'budget_notification_message') {
      // Если перевод не найден, возвращаем исходное сообщение
      return message;
    }
    
    // Ручная подстановка переменных
    return template
      .replace('{percentage}', localizedData.percentage)
      .replace('{category}', localizedData.category)
      .replace('{remaining}', localizedData.remaining);
  }
  // Для других типов уведомлений
  const messageMap = {
    'daily_reminder': 'daily_reminder_message',
    'weekly_reminder': 'weekly_reminder_message',
    'goal_achieved': 'goal_achieved_message',
    'goal_progress': 'goal_progress_message',
    'system_update': 'system_update_message',
    'expense_added': 'expense_notification_message',
    'income_added': 'income_notification_message',
    'backup_completed': 'backup_notification_message'
  };
  
  const translationKey = messageMap[type];
  if (translationKey && t(translationKey) !== translationKey) {
    return t(translationKey);
  }
  
  // Если всё не удалось, возвращаем исходное сообщение
  return message;
};
// Завершаем функцию getLocalizedTitle
const getLocalizedTitle = (notification) => {
  const { type, title } = notification;
  
  const titleMap = {
    'budget_exceeded': 'budget_exceeded',
    'budget': 'budget_warning',
    'daily_reminder': 'daily_reminder',
    'weekly_reminder': 'weekly_reminder',
    'goal_achieved': 'goal_achieved',
    'goal_progress': 'goal_progress',
    'system_update': 'system_update',
    'expense_added': 'expense_added',
    'income_added': 'income_added',
    'backup_completed': 'backup_completed'
  };
  
  // Проверяем, есть ли прямой перевод для заголовка
  if (title && t(title) !== title) {
    return t(title);
  }
  
  // Используем тип уведомления для определения ключа перевода
  const translationKey = titleMap[type];
  if (translationKey && t(translationKey) !== translationKey) {
    return t(translationKey);
  }
  
  // Если ничего подходящего не найдено, возвращаем оригинальный заголовок
  return title;
};
  const markAllAsRead = async () => {
    if (!telegramUserId) return;
    
    try {
      await fetch('https://fftg.farodinprojects.org:3000/mark_all_notifications_read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: telegramUserId })
      });

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('[NotificationCenter] Error marking all notifications as read:', error);
    }
  };

  const showToastNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico'
      });
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(notification.title, {
            body: notification.message,
            icon: '/favicon.ico'
          });
        }
      });
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);

    if (diffInMinutes < 1) {
      return t('just_now') || 'Только что';
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)} ${t('minutes_ago') || 'мин назад'}`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} ${t('hours_ago') || 'ч назад'}`;
    } else {
      return date.toLocaleDateString();
    }
  };

const getNotificationIcon = (type) => {
  switch (type) {
    case 'budget': 
    case 'budget_exceeded':
    case 'expense': 
      return <BudgetAlertIcon />;
    case 'goal':
    case 'goal_achieved':
    case 'goal_progress':
      return <GoalProgressIcon />;
    case 'reminder':
    case 'daily_reminder':
    case 'weekly_reminder':
      return <ReminderClockIcon />;
    case 'system':
    case 'update':
    case 'info':
      return <SystemInfoIcon />;
    case 'success':
    case 'completed':
      return <SuccessIcon />;
    case 'warning':
    case 'alert':
      return <WarningIcon />;
    default: 
      return <SystemInfoIcon />;
  }
};

  if (!telegramUserId) {
    return null;
  }

  return (
    <>
      <NotificationIcon onClick={() => setIsOpen(!isOpen)}>
        <BellIconComponent hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <NotificationBadge pulse={unreadCount > 0}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </NotificationBadge>
        )}
      </NotificationIcon>

      <MobileOverlay 
        isOpen={isOpen} 
        onClick={() => setIsOpen(false)} 
      />

      <NotificationContainer isOpen={isOpen}>
        <NotificationHeader>
          <h3 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            {t('notifications') || 'Уведомления'}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {unreadCount > 0 && (
              <ActionButton onClick={markAllAsRead}>
                {t('mark_all_read') || 'Отметить все'}
              </ActionButton>
            )}
            <CloseButton onClick={() => setIsOpen(false)}>
              <CloseIcon />
            </CloseButton>
          </div>
        </NotificationHeader>

        <NotificationList>
          {isLoading ? (
            <div style={{ 
              padding: '60px 24px', 
              textAlign: 'center',
              color: '#8b9dc3',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}>
              <LoadingSpinner />
              <div>{t('loading') || 'Загрузка...'}</div>
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <EmptyBellIcon />
              </EmptyStateIcon>
              <div>
                <div style={{ 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  fontSize: '16px',
                  color: '#6c757d'
                }}>
                  {t('no_notifications') || 'Нет уведомлений'}
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  opacity: '0.8',
                  color: '#8b9dc3'
                }}>
                  {t('notifications_will_appear_here') || 'Уведомления будут появляться здесь'}
                </div>
              </div>
            </EmptyState>
          ) : (
            notifications.map(notification => (
              <NotificationItem
  key={notification.id}
  isRead={notification.is_read}
  onClick={() => !notification.is_read && markAsRead(notification.id)}
>
  <NotificationTitle>
    {getNotificationIcon(notification.type)}
    {getLocalizedTitle(notification)}
  </NotificationTitle>
  <NotificationMessage>{getLocalizedMessage(notification)}</NotificationMessage>
  <NotificationTime>{formatTime(notification.created_at)}</NotificationTime>
</NotificationItem>
            ))
          )}
        </NotificationList>
      </NotificationContainer>
    </>
  );
};

export default NotificationCenter;